// Lib
import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';

// Utils
import { scrollToBottom } from './scrollableParentUtils';

// How close to the bottom we need to be before we consider ourselves scrolled to the bottom
const SCROLL_TO_BOTTOM_THRESHOLD = 15;

/**
 * This hook ensures that the scrollable area remains scrolled to the bottom, if the child element
 *  or scrollable element changes size, or if the user starts dragging an item.
 */
const useKeepScrolledToBottom = (
    scrollableElementRef: React.MutableRefObject<HTMLDivElement>,
    childElementRef: React.MutableRefObject<HTMLDivElement>,
    isDragging?: boolean,
) => {
    const lastScrollableElementHeight = React.useRef(0);
    const lastScrollHeight = React.useRef(0);
    const isScrolledToBottomRef = React.useRef(true);

    /**
     * Keep track of the last scrollable element height and scroll height.
     */
    const updateScrollMeasurementRefs = React.useCallback((scrollableElement) => {
        if (!scrollableElement) return;

        lastScrollableElementHeight.current = scrollableElement.clientHeight;
        lastScrollHeight.current = scrollableElement.scrollHeight;
    }, []);

    /**
     * On scroll, check if we are still scrolled to the bottom, as long as the scroll isn't a result
     *  of the child or parent changing size.
     */
    const onScroll = React.useCallback(() => {
        if (!scrollableElementRef.current) return;

        const scrollHeight = scrollableElementRef.current.scrollHeight;
        const scrollableElementHeight = scrollableElementRef.current.clientHeight;

        // The child element has grown, causing a scroll event to fire - we should ignore this case
        if (scrollHeight > lastScrollHeight.current) return;
        if (scrollableElementHeight !== lastScrollableElementHeight.current) return;

        const availableScroll = scrollHeight - scrollableElementHeight;
        const currentScroll = scrollableElementRef.current.scrollTop;

        isScrolledToBottomRef.current = currentScroll >= availableScroll - SCROLL_TO_BOTTOM_THRESHOLD;
    }, []);

    /**
     * On resize, if we are scrolled to the bottom, scroll to the bottom again.
     */
    const onResize = React.useCallback(() => {
        if (!scrollableElementRef.current) return;

        updateScrollMeasurementRefs(scrollableElementRef.current);

        // If we're not already scrolled to the bottom, we don't need to do anything
        if (!isScrolledToBottomRef.current) return;

        if (!scrollableElementRef.current) return;

        // Ensure measurements are up to date before scrolling
        requestAnimationFrame(() => scrollToBottom(scrollableElementRef.current));
    }, []);

    /**
     * On mount, set up the scroll event listener and child element resize observer.
     */
    React.useEffect(() => {
        if (!scrollableElementRef.current) return;

        // Start the element scrolled to bottom
        scrollToBottom(scrollableElementRef.current);

        updateScrollMeasurementRefs(scrollableElementRef.current);

        const resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(scrollableElementRef.current);
        childElementRef.current && resizeObserver.observe(childElementRef.current);

        scrollableElementRef.current.addEventListener('scroll', onScroll);

        return () => {
            resizeObserver?.disconnect();
            scrollableElementRef.current?.removeEventListener('scroll', onScroll);
        };
    }, []);

    // Scroll to the bottom when starting a drag
    React.useEffect(() => {
        if (!isDragging || !scrollableElementRef.current) return;

        scrollToBottom(scrollableElementRef.current);
    }, [isDragging]);
};

export default useKeepScrolledToBottom;
